import {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Alert,
  Button,
  Box,
  Breadcrumbs,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Typography,
  ToggleButtonGroup,
  TextField,
  ToggleButton,
  Tooltip,
  useTheme,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import dayjs from "dayjs";
import goToHelpLink from "../../../../utils/goToHelpLink";

import { FONT_PROPS, COLORS } from "../../styles/colors";

const SAFE_HELP_BASE_URL = "https://help.ese-tech.com/ko/safe_system";

const SAFE_HELP_TITLE_ANCHORS = {
  홈: "page-3-0",
  위험성평가: "page-4-0",
  "평가 Wizard": "page-4-0",
  "STEP 1 대상 선택/위험성평가": "page-4-0",
  "STEP 1 대상/위험성평가": "page-4-0",
  "STEP 2 개선대책": "page-4-0",
  "STEP 3 검토/승인": "page-4-0",
  "STEP 3 완료 처리/승인": "page-4-0",
  "작업 전 안전점검회의(TBM) 관리": "page-5-0",
  "TBM 관리": "page-5-0",
  "안전점검 관리": "page-5-0",
  "후속 조치 관리": "page-5-0",
  "근로자 안전보건교육 관리": "page-5-0",
  "안전보건교육 관리": "page-5-0",
  "검사/인증": "page-5-0",
  "안전보건위원회": "page-5-0",
  "기계/기구 유해·위험요인 등록": "page-5-0",
  "설비별 위험요인": "page-5-0",
  "근로자 유해·위험요인 제안": "page-7-0",
  "유해·위험요인 제안": "page-7-0",
  "내 조치 업무": "page-7-0",
  "근로자 유해·위험요인 제안 검토/승인": "page-5-0",
  "제안 검토/승인": "page-5-0",
  "라인 관리": "page-6-0",
  "대상 공정 등록": "page-6-0",
  "공정 관리": "page-6-0",
  "대상 기계/기구 정보": "page-6-0",
  "설비 관리": "page-6-0",
  "위험발생상황 및 결과": "page-6-0",
  "위험요인 관리": "page-6-0",
  "물질안전보건자료(MSDS) 관리": "page-6-0",
  "MSDS 관리": "page-6-0",
  "공정과 기계/기구 매칭": "page-6-0",
  "설비-공정 연결": "page-6-0",
  "위험성평가 주기 설정": "page-6-0",
  "평가 주기 설정": "page-6-0",
  "통합 보고서 자동생성": "page-8-0",
  보고서: "page-8-0",
  조직관리: "page-9-0",
};

export const getSafeHelpUrl = (title) => {
  const anchor = SAFE_HELP_TITLE_ANCHORS[title] || "page-1-0";
  return `${SAFE_HELP_BASE_URL}#${anchor}`;
};

export const ManualHelpButton = ({ title, url, sx = {} }) => {
  const targetUrl = url || getSafeHelpUrl(title);

  return (
    <Tooltip title="매뉴얼 보기">
      <Box
        component="span"
        onClick={(event) => {
          event.stopPropagation();
          goToHelpLink(targetUrl);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          color: "text.secondary",
          cursor: "pointer",
          transition: "background-color 120ms ease, color 120ms ease",
          "&:hover": {
            bgcolor: "action.hover",
            color: "primary.main",
          },
          ...sx,
        }}
      >
        <HelpOutlineIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

///////////////////////////////
// 공통 컴포넌트들
///////////////////////////////

// 스낵바 알람 (serverity: 'success' | 'error' | 'warning' | 'info')
export const SnackbarAlert = ({
  open,
  onClose,
  message,
  severity = "info",
  autoHideDuration = 2500,
  anchorOrigin = { vertical: "bottom", horizontal: "center" },
  snackBarSX = {},
  alertSX = {},
}) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      sx={{ ...snackBarSX }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        sx={{ width: "100%", ...alertSX }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// 컨펌 메시지 모달(버튼 2개)
export const ConfirmDialog = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  children,
}) => {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>취소</Button>
        <Button onClick={onConfirm} color="primary" variant="contained">
          {" "}
          확인{" "}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 컨펌 메시지 모달(버튼 1개)
export const ConfirmDialogSingle = ({ open, title, message, onCancel }) => {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary" variant="contained">
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 글머리를 지원하는 멀티라인 텍스트필드 컴포넌트
const BULLETS = ["•", "–", "○", "▣"]; // 원하는 4종
export const BulletTextField = forwardRef(function BulletTextField(
  {
    value: valueProp, // 컨트롤드 값 (옵션)
    defaultBullet = BULLETS[0], // 초기 글머리
    onChange, // 컨트롤드 변경 콜백 (옵션) => onChange(rawText, parsedItems)
    onBulletChange, // 글머리 바뀔 때 콜백 (옵션)
    minRows = 3, // 자동 높이 시작 줄 수
    maxRows = 8, // 자동 높이 최대 줄 수
    placeholder = "Enter=다음 줄 자동 글머리, Shift+Enter=일반 줄바꿈",
  },
  ref
) {
  const [bullet, setBullet] = useState(defaultBullet);
  const [value, setValue] = useState(valueProp ?? "");
  const textareaRef = useRef(null);

  // 외부 valueProp이 바뀌면 동기화
  useEffect(() => {
    if (valueProp !== undefined && valueProp !== value) {
      setValue(valueProp);
    }
  }, [valueProp]);

  const prefix = () => `${bullet} `;

  const getLineBoundaries = (text, pos) => {
    const start = text.lastIndexOf("\n", Math.max(0, pos - 1)) + 1;
    const endIdx = text.indexOf("\n", pos);
    const end = endIdx === -1 ? text.length : endIdx;
    return { start, end };
  };

  // 부모로 변경사항 알림 (raw + bullet 제거한 items 배열)
  const emitChange = (nextVal) => {
    if (!onChange) return;
    const bulletSet = new Set(BULLETS);
    const items = nextVal.split("\n").map((line) => {
      // "• " 같은 접두어 제거
      if (line.length >= 2 && bulletSet.has(line[0]) && line[1] === " ") {
        return line.slice(2);
      }
      return line;
    });
    onChange(nextVal, items);
  };

  const ensurePrefixOnCurrentLine = () => {
    const el = textareaRef.current;
    if (!el) return;
    const pos = el.selectionStart;
    const { start } = getLineBoundaries(value, pos);
    const has = value.startsWith(prefix(), start);
    if (has) return;

    const newVal = value.slice(0, start) + prefix() + value.slice(start);
    const newCaret = pos + prefix().length;
    setValue(newVal);
    emitChange(newVal);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = newCaret;
    });
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    setValue(next);
    emitChange(next);
  };

  const BULLET_SET = new Set(BULLETS);
  // 현재 줄이 글머리로 시작하면 접두어 길이를 돌려줌 (예: "• " => 2)
  const detectPrefix = (line) => {
    if (line.length >= 2 && BULLET_SET.has(line[0]) && line[1] === " ") {
      return { prefixStr: line.slice(0, 2), prefixLen: 2 };
    }
    return { prefixStr: "", prefixLen: 0 };
  };

  const handleKeyDown = (e) => {
    const el = textareaRef.current;
    if (!el) return;
    const pos = el.selectionStart;
    const { start } = getLineBoundaries(value, pos);

    if (e.key === "Enter") {
      if (e.shiftKey) return; // 글머리 없이 줄바꿈
      e.preventDefault();
      const hasPrefix = value.startsWith(prefix(), start);
      const ins = "\n" + (hasPrefix ? prefix() : "");
      const newVal = value.slice(0, pos) + ins + value.slice(pos);
      const newCaret = pos + ins.length;
      setValue(newVal);
      emitChange(newVal);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = newCaret;
      });
      return;
    }

    if (e.key === "Backspace") {
      const selStart = el.selectionStart;
      const selEnd = el.selectionEnd;
      if (selStart !== selEnd) return; // 드래그 선택시 기본 삭제

      const { start, end } = getLineBoundaries(value, pos);
      const line = value.slice(start, end);
      const { prefixLen } = detectPrefix(line);

      // (A) 글머리 뒤(접두어 바로 끝)에서 Backspace → 이전 줄과 병합
      if (prefixLen > 0 && pos === start + prefixLen) {
        // 이전 줄이 존재할 때만 merge; 없으면 글머리만 제거
        if (start > 0) {
          e.preventDefault();
          // 이전 줄의 마지막 문자 바로 뒤(즉, '\n' 직전)까지 유지 + 현재 줄에서는 접두어 제거
          const before = value.slice(0, start - 1); // 이전 줄 끝까지 (개행 제외)
          const after = value.slice(start + prefixLen); // 현재 줄에서 접두어 제거한 나머지
          const newVal = before + after;
          const newCaret = before.length; // 윗줄 끝으로 커서 이동
          setValue(newVal);
          emitChange?.(newVal);
          requestAnimationFrame(() => {
            el.selectionStart = el.selectionEnd = newCaret;
          });
          return;
        } else {
          // 문서 첫 줄이면 병합 대상 없음 → 글머리만 제거
          e.preventDefault();
          const newVal = value.slice(0, start) + value.slice(start + prefixLen);
          const newCaret = start;
          setValue(newVal);
          emitChange?.(newVal);
          requestAnimationFrame(() => {
            el.selectionStart = el.selectionEnd = newCaret;
          });
          return;
        }
      }

      // (B) 글머리 영역 안(라인 시작 ~ 접두어 끝) 이거나, 글머리만 있는 빈 줄 → 글머리 통째 제거
      const onlyBullet = prefixLen > 0 && line.length === prefixLen;
      const inBulletZone =
        prefixLen > 0 && pos >= start && pos <= start + prefixLen;
      if (onlyBullet || inBulletZone) {
        e.preventDefault();
        const newVal = value.slice(0, start) + value.slice(start + prefixLen);
        const newCaret = start;
        setValue(newVal);
        emitChange?.(newVal);
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = newCaret;
        });
        return;
      }

      // (C) 그 외엔 기본 백스페이스(한 글자 삭제)
    }

    if (e.key === "Tab") {
      e.preventDefault();
      const ins = e.shiftKey ? "" : "  ";
      const newVal = value.slice(0, pos) + ins + value.slice(pos);
      const newCaret = pos + ins.length;
      setValue(newVal);
      emitChange(newVal);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = newCaret;
      });
      return;
    }
  };

  const handleFocus = () => {
    ensurePrefixOnCurrentLine();
  };

  const handleClickOrSelect = () => {
    const el = textareaRef.current;
    if (!el) return;
    const pos = el.selectionStart;
    const { start, end } = getLineBoundaries(value, pos);
    const line = value.slice(start, end);
    if (line === "") {
      const newVal = value.slice(0, start) + prefix() + value.slice(start);
      const newCaret = pos + prefix().length;
      setValue(newVal);
      emitChange(newVal);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = newCaret;
      });
    }
  };

  const handleBulletSelect = (_e, next) => {
    if (!next) return;
    const el = textareaRef.current;
    const pos = el ? el.selectionStart : 0;
    const { start } = getLineBoundaries(value, pos);

    const prevPrefix = `${bullet} `;
    const nextPrefix = `${next} `;

    setBullet(next);
    onBulletChange && onBulletChange(next);

    const hasPrev = value.startsWith(prevPrefix, start);
    let newVal, newCaret;

    if (hasPrev) {
      newVal =
        value.slice(0, start) +
        nextPrefix +
        value.slice(start + prevPrefix.length);
      const caretShift = nextPrefix.length - prevPrefix.length;
      newCaret = pos + caretShift;
    } else {
      newVal = value.slice(0, start) + nextPrefix + value.slice(start);
      newCaret = pos + nextPrefix.length;
    }

    setValue(newVal);
    emitChange(newVal);
    requestAnimationFrame(() => {
      if (el) el.selectionStart = el.selectionEnd = newCaret;
    });
  };

  // ref로 메서드/값 노출 (부모에서 ref.current.getValue() 등 사용)
  useImperativeHandle(ref, () => ({
    getValue: () => value,
    getItems: () => {
      const bulletSet = new Set(BULLETS);
      return value.split("\n").map((line) => {
        if (line.length >= 2 && bulletSet.has(line[0]) && line[1] === " ") {
          return line.slice(2);
        }
        return line;
      });
    },
    setValue: (next) => {
      setValue(next ?? "");
      emitChange(next ?? "");
    },
    setBullet: (b) => setBullet(b),
    focus: () => textareaRef.current?.focus(),
  }));

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={bullet}
        onChange={handleBulletSelect}
      >
        {BULLETS.map((b) => (
          <ToggleButton key={b} value={b}>
            {b}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        inputRef={textareaRef}
        value={valueProp !== undefined ? valueProp : value} // 컨트롤드 우선
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onClick={handleClickOrSelect}
        onSelect={handleClickOrSelect}
        placeholder={placeholder}
        multiline
        minRows={minRows}
        maxRows={maxRows}
        fullWidth
      />
    </Box>
  );
});

// 테이블 상단 브레드크럼 + 타이틀 컴포넌트
export const BreadcrumbsTable = ({
  parentTitle,
  childTitle,
  useTab = false,
}) => {
  const theme = useTheme();

  const containerSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minWidth: 0,
    ...(useTab ? {} : { borderBottom: `1px solid ${theme.palette.divider}` }),
  };

  const titleSx = {
    padding: { xs: "14px 16px", md: "16px 20px", lg: "16px 32px" },
    fontWeight: FONT_PROPS.SEMIBOLD,
    lineHeight: FONT_PROPS.LINEHEIGHT,
    fontSize: { xs: "20px", md: "22px" },
    color: theme.palette.text,
    overflowWrap: "anywhere",
  };

  const breadcrumbTextSx = {
    fontSize: "14px",
    fontWeight: FONT_PROPS.MEDIUM,
    lineHeight: FONT_PROPS.LINEHEIGHT,
    color: FONT_PROPS.GRAY,
  };

  return (
    <Box sx={containerSx}>
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
        {/* 타이틀 표시 */}
        <Typography sx={titleSx}>{childTitle}</Typography>

        <ManualHelpButton title={childTitle} />
      </Box>

      {/* 카테고리 표시 */}
      <Breadcrumbs
        separator="›"
        aria-label="breadcrumb"
        sx={{
          padding: "16px 32px",
          display: { xs: "none", lg: "flex" },
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        <Typography key="1" sx={breadcrumbTextSx}>
          {parentTitle}
        </Typography>
        <Typography key="2" sx={breadcrumbTextSx}>
          {childTitle}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

/**
 * 접이식 카드 컴포넌트 템플릿
 * - 제목과 내용이 있는 카드로, 제목을 클릭하면 내용을 펼치거나 접을 수 있습니다.
 */
export function CollapsibleCardTemplate({
  children,
  title = "",
  isInitialOpen = false,
  containerSX = {},
  titleSX = {},
  contentSX = {},
  iconSX = {},
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(isInitialOpen);
  const handleOpenToggle = () => setOpen(!open);
  return (
    <Card
      sx={{
        width: "100%",
        height: "auto",
        //mx: 'auto',
        boxShadow: "0px 1px 4px rgba(0,0,0,0.07)",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "0.375rem",
        bgcolor: "var(--mui-palette-background-paper)",
        mb: 4,
        //overflow: 'hidden'
        ...containerSX,
      }}
    >
      {/* Header */}
      <Box
        onClick={handleOpenToggle}
        sx={{
          padding: "12px 24px",
          bgcolor: "rgba(229, 229, 236, 0.4)",
          cursor: "pointer",
          "&:hover": {
            bgcolor: "rgba(229, 229, 236, 0.6)",
          },
          fontSize: 18,
          fontWeight: FONT_PROPS.SEMIBOLD,
          ...titleSX,
        }}
      >
        <CardHeader
          title={
            <Typography
              sx={{
                fontSize: "inherit",
                fontWeight: "inherit",
                color: "inherit",
              }}
            >
              {title}
            </Typography>
          }
          action={
            <IconButton
              onClick={(e) => {
                e.stopPropagation(); // 이벤트 전파 방지
                handleOpenToggle();
              }}
              sx={{ ...iconSX }}
            >
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          sx={{
            "& .MuiCardHeader-action": {
              alignSelf: "center",
            },
            padding: "0px",
          }}
        />
      </Box>

      {/* 펼쳐지는 영역 */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        {/* <Box sx={{ backgroundColor: '#ffffff' }}> */}
        <CardContent sx={{ ...contentSX }}>{children}</CardContent>
        {/* </Box> */}
      </Collapse>
    </Card>
  );
}

///////////////////////////////
// 공통 유틸 함수들
///////////////////////////////

// 데이터 깊은 복사
export const deepCopy = (o) => JSON.parse(JSON.stringify(o));

// 파라미터를 배열로 정규화
export const normalizeToArray = (param) => {
  if (Array.isArray(param)) {
    // 이미 배열이면 그대로 반환
    return param;
  } else if (param && typeof param === "object") {
    // 객체라면 배열로 변환
    return [param];
  } else {
    // 그 외 값은 빈 배열로 처리
    return [];
  }
};

// 날짜 데이터 포맷 변경 (dayjs -> date)
export const formatDateWithDayjs = (value) => {
  if (!value) return null;
  const dayjsValue = dayjs(value);
  if (!dayjsValue.isValid()) return null;

  return dayjsValue.format("YYYY-MM-DD HH:mm:ss");
};
// 날짜 데이터 포맷 변경 (date -> dayjs)
export const formatDateToDayjs = (value) => {
  if (!value) return null;
  const dayjsValue = dayjs(value);
  if (!dayjsValue.isValid()) return null;

  return dayjsValue;
};
// 오늘 날짜 반환 (YYYY-MM-DD HH:mm:ss)
export const getTodayByDayjs = () => {
  return dayjs().format("YYYY-MM-DD HH:mm:ss");
};
